import React, { useState } from 'react';
import { Check, FileText, Search, Send, ClipboardList, Scale, Briefcase, Shield } from 'lucide-react';
import RegistrationModal from './RegistrationModal';

const TrademarkPricingSection = () => {
  const [activeTab, setActiveTab] = useState('self-filing');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');
  const [selectedPackage, setSelectedPackage] = useState({ title: '', amount: '' });

  const openModal = (type, packageTitle = '', packageAmount = '') => {
    setModalType(type);
    setSelectedPackage({ title: packageTitle, amount: packageAmount });
    setIsModalOpen(true);
  };

  // Step cards data for each tab
  const selfFilingSteps = [
    {
      title: 'Application Preparation',
      icon: FileText,
      description: 'Global Trademark Filer reviews your filing information, identifies the correct trademark class, and prepares your application for submission through the USPTO TEAS system.'
    },
    {
      title: 'Identity Review & Approval',
      icon: Search,
      description: 'You complete ID.me verification, then check the attorney-prepared application in detail. Any required edits or changes can be requested before submission.'
    },
    {
      title: 'USPTO Filing',
      icon: Send,
      description: 'After approval, Global Trademark Filer submits your application to the USPTO, ensuring every detail is accurate, complete, and ready for official review.'
    }
  ];

  const attorneyLedSteps = [
    {
      title: 'Information Collection',
      icon: ClipboardList,
      description: 'We collect your trademark details, including name, logo (if applicable), and a brief description of your products/services.'
    },
    {
      title: 'Attorney Review & Application Drafting',
      icon: Scale,
      description: 'Our IP attorney reviews your case, determines the appropriate trademark class(es), and prepares a strong, USPTO-compliant application.'
    },
    {
      title: 'Filing & Case Management',
      icon: Briefcase,
      description: 'We file the application on your behalf and handle all procedural steps, including monitoring your application and managing any USPTO correspondence.'
    }
  ];

  // Pricing plans for Self Filing tab (4 cards)
  const selfFilingPlans = [
    {
      name: 'Basic',
      price: '45',
      priceNote: '$45+USPTO fees',
      description: 'Essential trademark registration services',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured',
        'Direct Hit Search',
        'Dedicated Case Manager'
      ],
      popular: false
    },
    {
      name: 'Deluxe',
      price: '145',
      priceNote: '$145+USPTO fees',
      description: 'Enhanced protection with expert review',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured',
        'Direct Hit Search',
        'Dedicated Case Manager'
      ],
      popular: true
    },
    {
      name: 'Premium',
      price: '299',
      priceNote: '$299+USPTO fees',
      description: 'Comprehensive IP protection package',
      features: [
        'Everything in Deluxe +',
        'Case Review (by a TM Expert)',
        'Basic Free Trademark Search',
        'Trademark Monitoring Services (3 Months)',
        'Trademark Secured',
        'Refusal Risk Meter',
        'Case filing service with complete paperwork'
      ],
      popular: false
    },
    {
      name: 'Elite',
      price: '599',
      priceNote: '$599+USPTO fees',
      description: 'Premium full-service trademark protection',
      features: [
        'Case Review & Filing (U.S Registered IP Attorney)',
        'Assigned Case Filing Officer',
        'Private Registration',
        'Trademark Secured',
        'Federal Search & Clearance Report (All States)',
        'Federal E-Filing (USPTO)',
        'Cease & Desist Letter',
        'Same Day Filing',
        'Trademark Monitoring (1 Year)',
        'Direct Hit Search',
        'Dedicated Case Manager',
        '24/7 Secure Online Access',
        '100% Approval Guarantee'
      ],
      popular: false
    }
  ];

  // Pricing plans for Attorney-Led tab (4 cards)
  const attorneyLedPlans = [
    {
      name: 'Basic',
      price: '795',
      priceNote: '+ Federal Filing Fee',
      description: 'Reliable professional support for a smoother and more confident filing process',
      features: [
        'Attorney-Handled Trademark Filing from Start to Finish',
        'Professional Case Evaluation',
        'Application Preparation & Submission',
        'Preliminary Trademark Search',
        'State-Level E-Filing Included',
        'Attorney-Handled Trademark Filing from Start to Finish',
        'Professional Case Evaluation',
        'Application Preparation & Submission',
        'Preliminary Trademark Search',
        'State-Level E-Filing Included'
      ],
      popular: false
    },
    {
      name: 'Deluxe',
      price: '895',
      priceNote: '+ Federal Filing Fee',
      description: 'Guided step-by-step assistance with added care, clarity, and peace of mind',
      features: [
        'Attorney-Led Trademark Filing',
        'End-to-End Case Handling',
        'Comprehensive Case Review & Analysis',
        'Professional Application Preparation',
        'Trademark Secured',
        'USPTO-Compliant Filing Process',
        'Attorney-Led Trademark Filing',
        'End-to-End Case Handling',
        'Comprehensive Case Review & Analysis'
      ],
      popular: true
    },
    {
      name: 'Premium',
      price: '1,145',
      priceNote: '+ Federal Filing Fee',
      description: 'Organized service with expert review, careful preparation, and steady progress',
      features: [
        'Attorney-Led Case Review',
        'Complete Case Filing & Management',
        'Advanced Direct-Hit Trademark Search',
        'Trademark Monitoring & Alerts',
        'Comprehensive Risk & Refusal Meter',
        'Attorney-Led Case Review',
        'Complete Case Filing & Management',
        'Advanced Direct-Hit Trademark Search',
        'Trademark Monitoring & Alerts',
        'Comprehensive Risk & Refusal Meter'
      ],
      popular: false
    },
    {
      name: 'Signature',
      price: '1,545',
      priceNote: 'Federal Filing Fee Included',
      description: 'Priority support with focused attention, quicker handling, and greater confidence',
      features: [
        'Attorney-Led Case Review & Strategy',
        'Complete Case Filing & Management',
        'Advanced Direct-Hit Trademark Search',
        'Federal & State Trademark Search Report',
        'Trademark Monitoring & Real-Time Alerts',
        'Attorney-Led Case Review & Strategy',
        'Complete Case Filing & Management',
        'Advanced Direct-Hit Trademark Search',
        'Federal & State Trademark Search Report',
        'Trademark Monitoring & Real-Time Alerts'
      ],
      popular: false
    }
  ];

  const renderStepCards = (steps) => (
    <>
      {/* Desktop: 3 columns */}
      <div className="hidden md:grid grid-cols-3 gap-6 mb-12">
        {steps.map((step, index) => (
          <div
            key={index}
            className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100"
          >
            <div className="w-12 h-12 bg-[#168356]/10 rounded-lg flex items-center justify-center mb-4">
              <step.icon className="w-6 h-6 text-[#168356]" />
            </div>
            <h3 className="text-lg font-bold text-black mb-2">{step.title}</h3>
            <p className="text-gray-600 text-sm">{step.description}</p>
          </div>
        ))}
      </div>

      {/* Mobile: Horizontal scroll slider */}
      <div className="md:hidden mb-12">
        <div className="overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4">
          <div className="flex gap-4" style={{ width: 'max-content' }}>
            {steps.map((step, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-md border border-gray-100 w-[300px] flex-shrink-0"
              >
                <div className="w-12 h-12 bg-[#168356]/10 rounded-lg flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-[#168356]" />
                </div>
                <h3 className="text-lg font-bold text-black mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const renderPricingCards = (plans, tabType) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {plans.map((plan, index) => (
        <div
          key={index}
          className={`bg-white rounded-2xl p-6 ${plan.popular ? 'ring-2 ring-[#168356] shadow-2xl transform scale-105' : 'shadow-lg'} flex flex-col transition-all duration-300`}
        >
          {plan.popular && (
            <div className="bg-[#168356] text-white text-xs font-semibold px-3 py-1 rounded-full inline-block mb-4 text-center">
              Most Popular
            </div>
          )}
          <h3 className="text-xl font-bold text-black mb-2">{plan.name}</h3>
          <p className="text-gray-600 mb-4 text-sm min-h-[40px]">{plan.description}</p>
          <div className="mb-4">
            <span className="text-3xl font-bold text-black">${plan.price}</span>
            <p className="text-sm text-gray-600 mt-1">{plan.priceNote}</p>
          </div>
          <button
            onClick={() => openModal('registration', plan.name, plan.price)}
            className={`w-full py-3 text-sm font-semibold mb-6 rounded-lg transition-all ${plan.popular ? 'bg-[#168356] hover:bg-[#0a5938] text-white' : 'bg-black hover:bg-gray-800 text-white'}`}
          >
            Get Started
          </button>
          <ul className="space-y-3 flex-grow overflow-y-auto max-h-[200px] pr-2 trademark-features-scrollbar">
            {plan.features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-[#168356] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 text-sm leading-tight">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <div className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Introduction Section */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Brand Protection Made Simple
          </h2>
          <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Global Trademark Filer helps businesses secure their identity through reliable filing, monitoring, renewals, and complete trademark support. Whether your goal is to protect a company name, claim ownership of a logo, or safeguard a slogan, our team makes the process clear and manageable. We assist brands across all 50 U.S. states and international markets, helping keep your intellectual property protected wherever your business operates.
          </p>
        </div>

        {/* Tabs Navigation */}
        <div className="flex justify-center mb-12">
          <div className="bg-white rounded-xl p-1 shadow-md inline-flex">
            <button
              onClick={() => setActiveTab('self-filing')}
              className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${activeTab === 'self-filing'
                  ? 'bg-[#168356] text-white shadow-md'
                  : 'text-gray-600 hover:text-[#168356] hover:bg-gray-50'
                }`}
              aria-pressed={activeTab === 'self-filing'}
            >
              Self Filing with Assistance
            </button>
            <button
              onClick={() => setActiveTab('attorney-led')}
              className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${activeTab === 'attorney-led'
                  ? 'bg-[#168356] text-white shadow-md'
                  : 'text-gray-600 hover:text-[#168356] hover:bg-gray-50'
                }`}
              aria-pressed={activeTab === 'attorney-led'}
            >
              IP Attorney-Led Filing
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="transition-all duration-500 ease-in-out">
          {activeTab === 'self-filing' && (
            <div className="animate-fadeIn">
              {/* Step Cards */}
              {renderStepCards(selfFilingSteps)}

              {/* Pricing Section Heading */}
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-bold text-black pb-4">
                  Self-Filing with Expert Assistance and Complete Support<br />Plan for Easy Filing
                </h3>
              </div>

              {/* Pricing Cards */}
              {renderPricingCards(selfFilingPlans, 'self-filing')}
            </div>
          )}

          {activeTab === 'attorney-led' && (
            <div className="animate-fadeIn">
              {/* Step Cards */}
              {renderStepCards(attorneyLedSteps)}

              {/* Pricing Section Heading */}
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-bold text-black pb-4">
                  Attorney Led Filing with Complete End-to-End Service Plan
                </h3>
              </div>

              {/* Pricing Cards */}
              {renderPricingCards(attorneyLedPlans, 'attorney-led')}
            </div>
          )}
        </div>
      </div>

      {/* Registration Modal with tmSignUpType based on active tab */}
      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
        tmSignUpType={activeTab === 'self-filing' ? 1 : 2}
        packageTitle={selectedPackage.title}
        packageAmount={selectedPackage.amount}
      />
    </div>
  );
};

export default TrademarkPricingSection;
